/**
 * Block Editor Component
 * Embeds existing element editors with simplified layouts for inline use
 *
 * For oneline layout: Text input is always enabled. Media types use the same
 * element editors (ImageEdit / AudioEdit / VideoEdit) below for Upload/Link.
 */

import * as React from 'react';
import { cn, Textarea, Label, RichTextEditor, isTiptapDocument } from '@ai-content-hub/content-kit';
import type { TiptapJSON } from '@ai-content-hub/content-kit';
import type { BlockContent, BlockContentType, BlockContentAlignment } from './types';
import {
    toTextElementData,
    toImageElementData,
    toAudioElementData,
    toVideoElementData,
    toRichTextElementData,
    fromTextElementData,
    fromImageElementData,
    fromAudioElementData,
    fromVideoElementData,
    fromRichTextElementData,
    createDefaultBlockContent,
} from './types';
import { BlockTypeSelector } from './BlockTypeSelector';

// Import existing element editors
import { TextEdit } from '../text/TextEdit';
import { ImageEdit, type ImageEditConfig } from '../image/ImageEdit';
import { AudioEdit } from '../audio/AudioEdit';
import { VideoEdit } from '../video/VideoEdit';
import { RichTextEdit } from '../rich-text/RichTextEdit';
import { OfficeDocumentEdit } from '../office-document/OfficeDocumentEdit';

// Import element data types
import type { TextElementData } from '../text/schema';
import type { ImageElementData } from '../image/schema';
import type { AudioElementData } from '../audio/schema';
import type { VideoElementData } from '../video/schema';
import type { RichTextElementData } from '../rich-text/schema';

/** Layout types for block editor */
export type BlockEditorLayout = 'default' | 'oneline';

export interface BlockEditorProps {
    /** Content to edit */
    content: BlockContent;
    /** Callback when content changes */
    onChange: (content: BlockContent) => void;
    /** Callback when content type changes */
    onTypeChange?: (type: BlockContentType) => void;
    /** Whether editor is disabled */
    disabled?: boolean;
    /** Additional class names for container */
    className?: string;
    /** Compact mode - simplified layout (for default layout) */
    compact?: boolean;
    /** Layout mode */
    layout?: BlockEditorLayout;
    /** Label for the input (oneline layout) */
    label?: string;
    /** Placeholder text (oneline layout) */
    placeholder?: string;
    /** Allowed content types */
    allowedTypes?: BlockContentType[];
    /** Field name for identification */
    name?: string;
    /** Default alignment when creating new block content (e.g. on type change) */
    defaultAlignment?: BlockContentAlignment;
    /** Configuration passed to ImageEdit when content type is image */
    imageConfig?: ImageEditConfig;
}

/**
 * Get text from any block content type
 * For text type, returns the text field
 * For media types, returns title/alt/caption
 */
function getTextFromContent(content: BlockContent): string {
    switch (content.type) {
        case 'text':
            return content.text || '';
        case 'image':
            return content.alt || '';
        case 'audio':
            return content.title || '';
        case 'video':
            return content.title || '';
        case 'rich-text':
            return '';
        case 'office-document':
            return content.title || '';
        default:
            return '';
    }
}

/**
 * Update text in any block content type
 */
function updateTextInContent(content: BlockContent, text: string): BlockContent {
    switch (content.type) {
        case 'text':
            return { ...content, text };
        case 'image':
            return { ...content, alt: text };
        case 'audio':
            return { ...content, title: text };
        case 'office-document':
            return { ...content, title: text };
        case 'video':
            return { ...content, title: text };
        default:
            return content;
    }
}

/**
 * Block Editor that embeds existing element editors.
 * Uses the same data structure as the original elements.
 */
export const BlockEditor: React.FC<BlockEditorProps> = ({
    content,
    onChange,
    onTypeChange,
    disabled = false,
    className,
    compact = true,
    layout = 'default',
    label,
    placeholder = 'Enter text...',
    allowedTypes,
    defaultAlignment,
    imageConfig,
}) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    // Auto-resize textarea when content changes (one row default, grows with wrap)
    const textValue = getTextFromContent(content);
    React.useEffect(() => {
        if (layout !== 'oneline') return;
        const ta = textareaRef.current;
        if (!ta) return;
        ta.style.height = 'auto';
        ta.style.height = `${Math.max(ta.scrollHeight, 36)}px`;
    }, [layout, textValue]);

    // Handle type change
    const handleTypeChange = (type: BlockContentType) => {
        if (onTypeChange) {
            onTypeChange(type);
        }
        // Preserve text when changing types
        const currentText = getTextFromContent(content);
        const newContent = createDefaultBlockContent(
            type,
            defaultAlignment ? { alignment: defaultAlignment } : undefined
        );
        const updatedContent = updateTextInContent(newContent, currentText);
        onChange(updatedContent);
    };

    // Handle text change for oneline layout - works for all content types
    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const updatedContent = updateTextInContent(content, e.target.value);
        onChange(updatedContent);
    };

    // Render oneline layout
    if (layout === 'oneline') {
        // Rich-text type: compact inline editor with hidden toolbar
        if (content.type === 'rich-text') {
            const rtData = toRichTextElementData(content);
            const tiptapValue = isTiptapDocument(rtData.content)
                ? (rtData.content as TiptapJSON)
                : { type: 'doc' as const, content: [{ type: 'paragraph' as const }] };

            return (
                <div ref={containerRef} className={cn('block-editor block-editor--oneline', className)}>
                    {label && (
                        <Label
                            className={cn(
                                'block-editor-label',
                                'text-sm font-medium text-muted-foreground mb-1.5 block'
                            )}
                        >
                            {label}
                        </Label>
                    )}
                    <RichTextEditor
                        value={tiptapValue}
                        onChange={(data: TiptapJSON) =>
                            onChange(fromRichTextElementData({ content: data }))
                        }
                        readOnly={disabled}
                        hideToolbar
                        minHeight={36}
                        placeholder={placeholder}
                        className="block-editor-richtext-inline [&_.tiptap-content]:!py-1 [&_.tiptap-content]:!px-1 [&_.tiptap-content]:!min-h-[36px]"
                    />
                </div>
            );
        }

        return (
            <div ref={containerRef} className={cn('block-editor block-editor--oneline', className)}>
                {/* Label */}
                {label && (
                    <Label
                        className={cn(
                            'block-editor-label',
                            'text-sm font-medium text-muted-foreground mb-1.5 block'
                        )}
                    >
                        {label}
                    </Label>
                )}

                {/* Text input - Textarea for multiline, break-word for long text */}
                <div className={cn('block-editor-input-wrap', 'relative')}>
                    <Textarea
                        ref={textareaRef}
                        className={cn(
                            'block-editor-input w-full rounded-sm min-h-9 resize-none break-words overflow-hidden',
                            'pr-24 py-2'
                        )}
                        value={textValue}
                        onChange={handleTextChange}
                        placeholder={placeholder}
                        disabled={disabled}
                        rows={1}
                    />
                    <div
                        className="block-editor-toolbar absolute"
                        style={{
                            top: 2,
                            right: 1,
                        }}
                    >
                        <BlockTypeSelector
                            value={content.type}
                            onChange={handleTypeChange}
                            disabled={disabled}
                            allowedTypes={
                                allowedTypes?.filter((t) => t !== 'text') || [
                                    'image',
                                    'audio',
                                    'video',
                                ]
                            }
                            showText={false}
                            size="sm"
                            layout="media-merged"
                        />
                    </div>
                </div>

                {(content.type === 'image' ||
                    content.type === 'audio' ||
                    content.type === 'video') && (
                    <div className="block-editor-oneline-media mt-2 w-full min-w-0">
                        {content.type === 'image' && (
                            <ImageEdit
                                value={toImageElementData(content)}
                                onChange={(data: ImageElementData) =>
                                    onChange(fromImageElementData(data))
                                }
                                disabled={disabled}
                                imageConfig={imageConfig}
                            />
                        )}
                        {content.type === 'audio' && (
                            <AudioEdit
                                value={toAudioElementData(content)}
                                onChange={(data: AudioElementData) =>
                                    onChange(fromAudioElementData(data))
                                }
                                disabled={disabled}
                            />
                        )}
                        {content.type === 'video' && (
                            <VideoEdit
                                value={toVideoElementData(content)}
                                onChange={(data: VideoElementData) =>
                                    onChange(fromVideoElementData(data))
                                }
                                disabled={disabled}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    }

    // Default layout - render full element editors
    const wrapperClass = cn('block-editor', compact && 'block-editor--compact', className);

    switch (content.type) {
        case 'text':
            return (
                <div className={wrapperClass}>
                    <TextEdit
                        value={toTextElementData(content)}
                        onChange={(data: TextElementData) => onChange(fromTextElementData(data))}
                        disabled={disabled}
                    />
                </div>
            );

        case 'image':
            return (
                <div className={wrapperClass}>
                    <ImageEdit
                        value={toImageElementData(content)}
                        onChange={(data: ImageElementData) => onChange(fromImageElementData(data))}
                        disabled={disabled}
                        imageConfig={imageConfig}
                    />
                </div>
            );

        case 'audio':
            return (
                <div className={wrapperClass}>
                    <AudioEdit
                        value={toAudioElementData(content)}
                        onChange={(data: AudioElementData) => onChange(fromAudioElementData(data))}
                        disabled={disabled}
                    />
                </div>
            );

        case 'video':
            return (
                <div className={wrapperClass}>
                    <VideoEdit
                        value={toVideoElementData(content)}
                        onChange={(data: VideoElementData) => onChange(fromVideoElementData(data))}
                        disabled={disabled}
                    />
                </div>
            );

        case 'rich-text':
            return (
                <RichTextEdit
                    value={toRichTextElementData(content)}
                    onChange={(data: RichTextElementData) =>
                        onChange(fromRichTextElementData(data))
                    }
                    disabled={disabled}
                    className={wrapperClass}
                />
            );

        case 'office-document':
            return (
                <OfficeDocumentEdit
                    value={content}
                    onChange={onChange}
                    disabled={disabled}
                    className={wrapperClass}
                />
            );

        default:
            return null;
    }
};

export default BlockEditor;
